import axios from 'axios';
import { createErr, createOk, Result } from 'types/result';
import {
  isOriginalMenu,
  isOriginalMenus,
  MenuType,
  OriginalMenu,
} from 'types/menu';
import { isMenuCrawlers, MenuCrawler } from 'types/menuCrawler';
import { isMenuCustomers, MenuCustomer } from 'types/menuCustomer';
import { constructUrl } from './utils';

const URL = '/v1/menus';

/**
 * メニュー新規登録 payload
 */
export type MenuRegistrationPayload = {
  /** カテゴリーID */
  categoryId: number | null;
  /** メニューID */
  id: number | null;
  /** メニュー名 */
  name: string;
  /** 公開範囲 */
  type: MenuType;
  /** メニュー説明 */
  description: string;
  /** 並び順 */
  displayOrder: number;
};

/**
 * メニュー編集 payload
 */
export type MenuEditPayload = MenuRegistrationPayload;

/**
 * メニューに紐づくクローラー新規登録 payload
 */
export type MenuCrawlersEditPayload = {
  /** メニューID */
  menuId: number;
  /** クローラーID */
  crawlerId: number;
  /** 先頭のクローラーかどうか */
  firstCrawler: boolean;
  /** 先頭に続くクローラーのID */
  targetCrawlers: number[] | null;
  /** 表示順 */
  displayOrder: number;
}[];

/**
 * メニュー一覧を取得する
 * @returns メニュー一覧
 */
export const fetchMenus = async (): Promise<Result<OriginalMenu[], Error>> => {
  const url = constructUrl(URL);

  return axios
    .get(url)
    .then((res) => {
      const data = res.data as unknown;

      if (!isOriginalMenus(data)) {
        throw Error(`Unsupported Value: ${JSON.stringify(data)}`);
      }

      return createOk(data);
    })
    .catch((err: Error) => createErr(err));
};

/**
 * メニューを登録する
 */
export const addMenu = async (
  payload: MenuRegistrationPayload,
): Promise<Result<OriginalMenu, Error>> =>
  axios
    .post(constructUrl(URL), payload)
    .then((res) => {
      const data = res.data as unknown;

      if (!isOriginalMenu(data)) {
        throw Error(`Unsupported Value: ${JSON.stringify(data)}`);
      }

      return createOk(data);
    })
    .catch((err: Error) => createErr(err));

/**
 * メニューを更新する
 */
export const editMenu = async (
  menuId: number,
  payload: MenuEditPayload,
): Promise<Result<OriginalMenu, Error>> =>
  axios
    .put(constructUrl(`${URL}/${menuId}`), payload)
    .then((res) => {
      const data = res.data as unknown;

      if (!isOriginalMenu(data)) {
        throw Error(`Unsupported Value: ${JSON.stringify(data)}`);
      }

      return createOk(data);
    })
    .catch((err: Error) => createErr(err));

/**
 * メニューを削除する
 */
export const deleteMenu = async (
  menuId: number,
): Promise<Result<null, Error>> =>
  axios
    .delete(constructUrl(`${URL}/${menuId}`))
    .then(() => createOk(null))
    .catch((err: Error) => createErr(err));

/**
 * メニューに紐づくクローラーを取得
 */
export const fetchMenuCrawlers = async (
  menuId: number,
): Promise<Result<MenuCrawler[], Error>> =>
  axios
    .get(constructUrl(`${URL}/${menuId}/crawlers`))
    .then((res) => {
      const data = res.data as unknown;

      if (!isMenuCrawlers(data)) {
        throw Error(`Unsupported Value: ${JSON.stringify(data)}`);
      }

      return createOk(data);
    })
    .catch((err: Error) => createErr(err));

/**
 * メニューに紐づくクローラーを更新
 */
export const editMenuCrawlers = async (
  menuId: number,
  payload: MenuCrawlersEditPayload,
): Promise<Result<MenuCrawler[], Error>> =>
  axios
    .put(constructUrl(`${URL}/${menuId}/crawlers`), payload)
    .then((res) => {
      const data = res.data as unknown;

      if (!isMenuCrawlers(data)) {
        throw Error(`Unsupported Value: ${JSON.stringify(data)}`);
      }

      return createOk(data);
    })
    .catch((err: Error) => createErr(err));

/**
 * メニューに紐づく顧客を取得
 */
export const fetchMenuCustomers = async (
  menuId: number,
): Promise<Result<MenuCustomer[], Error>> =>
  axios
    .get(constructUrl(`${URL}/${menuId}/customers`))
    .then((res) => {
      const data = res.data as unknown;

      if (!isMenuCustomers(data)) {
        throw Error(`Unsupported Value: ${JSON.stringify(data)}`);
      }

      return createOk(data);
    })
    .catch((err: Error) => createErr(err));
